/**
 * Trade Validation Utilities
 * Functions for validating trade data and form inputs
 */

import { TradeFormData, TradeValidationErrors, TradeStatus } from "../types/trade";

/**
 * Validate that a value is greater than zero
 */
export const validateGreaterThanZero = (
  value: number | undefined,
  fieldName: string
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (value <= 0) {
    return `${fieldName} must be greater than zero`;
  }
  return undefined;
};

/**
 * Validate that a required field is not empty
 */
export const validateRequired = (
  value: string | number | undefined,
  fieldName: string
): string | undefined => {
  if (value === undefined || value === null || value === "") {
    return `${fieldName} is required`;
  }
  return undefined;
};

/**
 * Validate date range (exit date must be >= entry date)
 * Allows same-day trades (e.g., scalp trades)
 */
export const validateDateRange = (
  entryDate: string,
  exitDate: string | undefined
): string | undefined => {
  if (!exitDate) {
    return undefined; // Exit date is optional for open trades
  }
  
  const entry = new Date(entryDate);
  const exit = new Date(exitDate);
  
  if (isNaN(entry.getTime())) {
    return "Invalid entry date";
  }
  
  if (isNaN(exit.getTime())) {
    return "Invalid exit date";
  }
  
  if (exit < entry) {
    return "Exit date must be on or after entry date";
  }
  
  return undefined;
};

/**
 * Validate that exit price is provided for closed trades
 */
export const validateExitPriceForClosedTrade = (
  status: TradeStatus,
  exitPrice: number | undefined
): string | undefined => {
  if (status === "closed" && (!exitPrice || exitPrice <= 0)) {
    return "Exit price is required for closed trades";
  }
  return undefined;
};

/**
 * Validate trade form data
 * Returns an object with validation errors (empty if valid)
 */
export const validateTradeForm = (
  formData: TradeFormData
): TradeValidationErrors => {
  const errors: TradeValidationErrors = {};
  
  // Required fields
  const symbolError = validateRequired(formData.symbol, "Symbol");
  if (symbolError) errors.symbol = symbolError;
  
  const entryDateError = validateRequired(formData.entryDate, "Entry date");
  if (entryDateError) errors.entryDate = entryDateError;
  
  const entryPriceError = validateRequired(formData.entryPrice, "Entry price");
  if (entryPriceError) {
    errors.entryPrice = entryPriceError;
  } else {
    const priceError = validateGreaterThanZero(formData.entryPrice, "Entry price");
    if (priceError) errors.entryPrice = priceError;
  }
  
  const entryQuantityError = validateRequired(formData.entryQuantity, "Entry quantity");
  if (entryQuantityError) {
    errors.entryQuantity = entryQuantityError;
  } else {
    const quantityError = validateGreaterThanZero(formData.entryQuantity, "Entry quantity");
    if (quantityError) errors.entryQuantity = quantityError;
  }
  
  // Exit date validation
  if (formData.exitDate) {
    const dateRangeError = validateDateRange(formData.entryDate, formData.exitDate);
    if (dateRangeError) errors.exitDate = dateRangeError;
  }
  
  // Exit date validation for closed trades
  if (formData.status === "closed") {
    const exitDateRequired = validateRequired(formData.exitDate, "Exit date");
    if (exitDateRequired) errors.exitDate = exitDateRequired;
  }

  // Exit price validation for closed trades
  if (formData.status === "closed") {
    const exitPriceError = validateExitPriceForClosedTrade(
      formData.status,
      formData.exitPrice
    );
    if (exitPriceError) errors.exitPrice = exitPriceError;
    
    if (formData.exitPrice) {
      const exitPriceGreaterError = validateGreaterThanZero(
        formData.exitPrice,
        "Exit price"
      );
      if (exitPriceGreaterError) errors.exitPrice = exitPriceGreaterError;
    }
  }
  
  // Exit quantity validation (if provided)
  if (formData.exitQuantity !== undefined) {
    const exitQuantityError = validateGreaterThanZero(
      formData.exitQuantity,
      "Exit quantity"
    );
    if (exitQuantityError) errors.exitQuantity = exitQuantityError;
  }
  
  return errors;
};

/**
 * Check if trade form is valid
 */
export const isTradeFormValid = (
  formData: TradeFormData
): boolean => {
  const errors = validateTradeForm(formData);
  return Object.keys(errors).length === 0;
};

